import { useEffect, useMemo, useState } from "react";
import { api, apiRequest, ApiError } from "../../lib/api";
import type {
  LolerGmailSweepResult,
  LolerSweepCandidate,
  LolerReportCreateInput,
  LolerDefectInput,
  LolerDefectCategory,
  ReportCompleteness,
} from "../../lib/api";
import { RecordPicker } from "../../components/RecordPicker";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { buildIntakeFields, hasLeadGenDetails, preContractFromHeader, type LolerIntakeMode, type PreContractDetails } from "./lolerPreContract";
import { PreContractFields } from "./PreContractFields";
import { buildLiftSpecFields, liftSpecFromHeader, type LiftSpecDetails } from "./lolerLiftSpec";
import { LiftSpecFields } from "./LiftSpecFields";
import { Button } from "../../components/Button";
import { formatDate } from "../../lib/format";

// Session AGF: the review queue for a Gmail sweep. One card per candidate PDF, pre-filled from the
// AI extraction, for the office to confirm (pick the lift, Create report) or Dismiss. NOTHING is
// auto-created: a report is created only when the office presses Create report and confirms who or
// what it is for.
//
// SESSION ALP: the sweep no longer searches for a known inspection body's own address -- it reads
// every PDF attachment in the window and the AI extraction decides which ones genuinely are LOLER
// reports, because the ordinary real case is an inspection body reporting to a duty holder or an RTM
// director, who then forwards it to ELE from their own address (Director-confirmed). So a candidate's
// emailFrom is now usually the actual sender, not an inspection body, and notReportCount counts PDFs
// that were read but correctly excluded rather than shown as noise.
//
// TWO PATHS, not one (Session ALH). A swept report usually names a lift ELE already maintains, and
// that path is unchanged. But the case the LOLER spec calls out as the one not to miss is the other
// one: a thorough-examination report emailed in by somebody who is NOT a customer yet, whose defects
// are both a remedial quote and the reason to put them on a maintenance contract. Until now the card
// could only be filed against an existing lift, so that report could not be filed from the sweep at
// all -- while the extraction had already read the enquirer's name, email, phone and site off the
// PDF and was carrying them, unused, in extractedHeader.
//
// The branch is decided by lolerPreContract.ts, shared with the manual New report modal, and files
// through the SAME office route (POST /api/loler/reports), which attaches a pre-contract enquiry to
// a Prospect via prospectIntake.ts. Nothing about the pre-contract path is reimplemented here.

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-body";


const CATEGORY_LABEL: Record<string, string> = { immediate: "Immediate danger", timed: "Remedy by date", advisory: "Advisory" };
const SKIP_MESSAGE: Record<string, string> = {
  too_large: "The PDF was too large to read automatically. Create the report manually from the New report button.",
  // Kept distinct from ai_failed so the office checks the right thing.
  download_failed: "The PDF could not be fetched from Gmail. Check the Google connection in Settings, then sweep again.",
  ai_failed: "The AI assistant could not read this PDF. Create the report manually from the New report button.",
  // Session ALQ: this PDF was found, not lost -- the AI-extraction budget for this run was already
  // spent on more recent candidates. Distinct wording from every other reason above, all of which
  // describe an attempt that failed; this one was deliberately deferred.
  extraction_capped: "This run's AI-extraction budget was already used on more recent emails. Sweep again shortly to check this one, or create the report manually from the New report button.",
  no_pdf: "No PDF attachment was found on this email.",
};
// A possible duplicate, never a certainty: one LOLER visit can examine several lifts on the same day,
// and an inspection body may reuse one reference across the per-lift certificates. So the card names
// what matched and stays actionable, rather than asserting the report is already on record.
const DUPLICATE_MESSAGE: Record<string, string> = {
  report_ref: "A report with this reference is already on record. Check it is the same lift: one reference can cover several lifts examined on the same visit.",
  exam_date_and_inspector:
    "A report with this examination date and inspector is already on record. This PDF carries no reference, so it may be a different lift examined on the same visit.",
};

const keyOf = (c: LolerSweepCandidate): string => `${c.gmailMessageId}:${c.gmailAttachmentId}`;

export function LolerGmailSweepModal({ result, onClose }: { result: LolerGmailSweepResult; onClose: () => void }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [createdKeys, setCreatedKeys] = useState<Set<string>>(new Set());
  // Collapsed only when there is other work in front of the office. When EVERY found PDF matched
  // something on record, the possible duplicates are the only thing to look at, so hiding them behind
  // a toggle is how a genuinely different lift's report gets missed (Session AGF review).
  const [showImported, setShowImported] = useState(() => result.candidates.length > 0 && result.candidates.every((c) => c.alreadyImported));

  const active = useMemo(() => result.candidates.filter((c) => !c.alreadyImported), [result.candidates]);
  const imported = useMemo(() => result.candidates.filter((c) => c.alreadyImported), [result.candidates]);
  const unresolvedActive = active.filter((c) => !dismissed.has(keyOf(c)) && !createdKeys.has(keyOf(c)));
  const allResolved = active.length > 0 && unresolvedActive.length === 0;

  // The summary counts what the office can actually ACT on, so a queue whose every PDF is a possible
  // duplicate never reads as "confirm the lift for each" over an empty list. It also never claims an
  // exhaustive all-clear when messages failed to read or the message cap was reached.
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const incomplete = result.readFailedCount > 0 || result.capped;
  let summary: string;
  if (active.length > 0) {
    // "Confirm the lift for each" until Session ALH, which was the one instruction a report from
    // somebody not on the platform yet cannot be followed for. It now names both paths.
    summary = `Found ${plural(active.length, "report PDF", "report PDFs")} to review across ${plural(result.messageCount, "email", "emails")}. Confirm who or what each one is for, then create the report. Nothing is created until you press Create report.`;
  } else if (result.candidateCount > 0) {
    // NOT "nothing to create": a match is only a POSSIBLE duplicate (one reference can cover several
    // lifts examined on one visit), so the office is told to check each rather than to close the modal.
    summary = `Found ${plural(result.candidateCount, "report PDF", "report PDFs")}, and each one matches a report already on record. Check each is the same lift before closing: one reference can cover several lifts examined on the same visit.`;
  } else if (incomplete) {
    summary = `No new LOLER report PDFs were read from ${plural(result.messageCount, "email", "emails")}.`;
  } else if (result.notReportCount > 0) {
    // Session ALP: discovery now reads every PDF in the window, so a genuinely empty result can mean
    // a lot was read and correctly excluded -- say so, rather than reading as "nothing was searched".
    summary = `Read ${plural(result.notReportCount, "attachment", "attachments")} across ${plural(result.messageCount, "email", "emails")} in the last 90 days, and none looked like a LOLER thorough-examination report.`;
  } else {
    summary = `No LOLER report PDFs found in the last 90 days (searched ${plural(result.messageCount, "email", "emails")}).`;
  }

  return (
    <ModalShell ariaLabel="Gmail sweep review" onClose={onClose} width="max-w-[640px]" margin="my-8" panelClassName="p-6">
      <h2 className="mb-1 font-heading text-2xl tracking-wide">Gmail sweep</h2>
      <p className="mb-3 text-sm text-ele-body">{summary}</p>

      {incomplete ? (
        // Session ALQ: role="alert" so this is impossible to miss, not just visually amber -- a
        // capped run must be something the office is actually told, never a quiet field. The capped
        // half of this message comes from the server's own capNotice (built once in gmailSweep.ts),
        // never a hand-typed number here that can go stale the next time the cap changes.
        <p role="alert" className="mb-4 rounded-ele border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-400">
          {result.readFailedCount > 0
            ? `${plural(result.readFailedCount, "email could", "emails could")} not be read this time, so ${result.readFailedCount === 1 ? "it" : "they"} may still hold a report. `
            : ""}
          {result.capNotice ? `${result.capNotice} ` : ""}
          {result.capNotice ? null : "Sweep again in a moment, or use New report to enter anything missing by hand."}
        </p>
      ) : null}


      {allResolved ? (
        <p className="mb-4 rounded-ele border border-ele-green bg-ele-inset px-3 py-2 text-sm text-ele-green">
          All done. {createdKeys.size} report{createdKeys.size === 1 ? "" : "s"} created.
        </p>
      ) : null}

      <div className="space-y-3">
        {active.map((c) =>
          dismissed.has(keyOf(c)) ? null : (
            <SweepCard
              key={keyOf(c)}
              candidate={c}
              created={createdKeys.has(keyOf(c))}
              onCreated={() => setCreatedKeys((s) => new Set(s).add(keyOf(c)))}
              onDismiss={() => setDismissed((s) => new Set(s).add(keyOf(c)))}
            />
          ),
        )}
      </div>

      {imported.length > 0 ? (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowImported((v) => !v)}
            className="font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
          >
            {showImported ? "Hide" : "Show"} possible duplicates ({imported.length})
          </button>
          {showImported ? (
            <div className="mt-3 space-y-3">
              {imported.map((c) =>
                dismissed.has(keyOf(c)) ? null : (
                  <SweepCard
                    key={keyOf(c)}
                    candidate={c}
                    created={createdKeys.has(keyOf(c))}
                    onCreated={() => setCreatedKeys((s) => new Set(s).add(keyOf(c)))}
                    onDismiss={() => setDismissed((s) => new Set(s).add(keyOf(c)))}
                  />
                ),
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={`gap-2 ${MODAL_ACTIONS}`}>
        <button onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>
          Close
        </button>
      </div>
    </ModalShell>
  );
}

function SweepCard({
  candidate,
  created,
  onCreated,
  onDismiss,
}: {
  candidate: LolerSweepCandidate;
  created: boolean;
  onCreated: () => void;
  onDismiss: () => void;
}) {
  const h = candidate.extractedHeader;
  const [assetId, setAssetId] = useState("");
  // Session SC2B: the lift is SEARCHED (the typeahead picker, seeded with the site the report names), not chosen from the whole register.
  // The amber "nothing matches" note below needs one fact the picker does not give: whether any lift on file matches that site name. It is
  // asked once, as a one-lift search, and the note waits for the answer (a search still in flight or one that failed claims nothing).
  const [siteSearch, setSiteSearch] = useState<"pending" | "none" | "found" | "failed">("pending");
  const siteTerm = (h.siteName ?? "").trim();
  // Which of the two things this report is for. Defaults to "lift", so a swept report behaves
  // exactly as it always has until the office says otherwise.
  const [mode, setMode] = useState<LolerIntakeMode>("lift");
  // The enquirer's details, pre-filled from the SAME extraction that fills the header fields above.
  // The model already read these off the PDF; before this they were carried to the browser and
  // dropped on Create, because nothing on this card could submit them.
  const [pre, setPre] = useState<PreContractDetails>(() => preContractFromHeader(h));
  // The lift's own technical specification, pre-filled from the same extraction (Session ALO).
  const [liftSpec, setLiftSpec] = useState<LiftSpecDetails>(() => liftSpecFromHeader(h));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fileWarn, setFileWarn] = useState(false);
  const [createdReportId, setCreatedReportId] = useState<string | null>(null);
  const [refiling, setRefiling] = useState(false);

  // The extraction PRE-FILLS an editable form; it never commits unseen (Session AGF review,
  // compliance lens). These fields set a statutory clock: examinationDate/nextExaminationDue drive a
  // people-lifting asset's lolerDue, which the daily reminder cron then asserts to the customer in an
  // automatic email, and completeness gates whether a remedial quote can be finalised. A read-only
  // card committed all of that exactly as the model read it, with no correction path in product, so
  // the office edits them here before creating the record -- the same shape as the manual New report
  // form, which has always pre-filled into editable inputs.
  const [form, setForm] = useState({
    inspectorName: h.inspectorName ?? "",
    examinationDate: h.examinationDate ?? "",
    nextExaminationDue: h.nextExaminationDue ?? "",
    reportRef: h.reportRef ?? "",
    completeness: (h.completeness ?? "full") as ReportCompleteness,
  });
  // Defects are editable too: the description travels into the remedial-quote line and the duty
  // holder's email once the office quotes it, and a "Remedy by date" item is only ever chased when it
  // carries a deadline. The extractor reads a deadline only where the report states one, so an
  // undated defect still arrives null for the office to set here.
  const [defects, setDefects] = useState<LolerDefectInput[]>(
    candidate.extractedDefects.map((d) => ({ description: d.description, category: d.category, deadline: d.deadline ?? null })),
  );

  function setField<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  useEffect(() => {
    if (siteTerm.length < 2) {
      setSiteSearch("found"); // nothing was searched for, so there is nothing to claim is missing
      return;
    }
    let alive = true;
    apiRequest<unknown[]>(`/assets?q=${encodeURIComponent(siteTerm)}&take=1&includeRetired=false`, { noCache: true })
      .then((rows) => {
        if (alive) setSiteSearch(rows.length === 0 ? "none" : "found");
      })
      .catch(() => {
        if (alive) setSiteSearch("failed");
      });
    return () => {
      alive = false;
    };
  }, [siteTerm]);

  // ONE question, asked once, answering three things: whether Create is live, the sentence saying
  // why it is not, and the body that gets sent. Asking the shared builder separately for each is how
  // a button comes to be enabled for a body the builder then refuses to make.
  //
  // An examination date is what makes the record a statutory record: without it the report is
  // excluded from the equipment clock, renders blank in the auditor export, and can never be deduped.
  // It is required on BOTH branches for the same reasons: a pre-contract enquiry is still a LOLER
  // record, and this is the field the sweep's own duplicate check reads.
  const intake = buildIntakeFields(mode, { assetId, details: pre });
  const blockedReason = !intake.ok ? intake.message : form.examinationDate ? null : "Enter the examination date from the report.";
  const canCreate = blockedReason === null;

  // Worth telling the office this may not be a lift on file: the register HAS been read, nothing in
  // it matches what they are searching for, and the extraction named an enquirer whose details only
  // the pre-contract branch would save. It offers the switch and never takes it: an existing
  // customer's report names a customer too, so which branch is right is the office's call, not a
  // guess the card is entitled to make (the Session ALG rule, applied to this surface).
  const suggestPreContract = mode === "lift" && !assetId && siteSearch === "none" && hasLeadGenDetails(h);

  async function fileAttachment(reportId: string): Promise<boolean> {
    try {
      await api.loler.fileGmailAttachment(reportId, {
        gmailMessageId: candidate.gmailMessageId,
        gmailAttachmentId: candidate.gmailAttachmentId,
        filename: candidate.filename,
      });
      return true;
    } catch {
      return false;
    }
  }

  async function create() {
    // Reuses the SAME answer the button was gated on, rather than asking again: the fields sent are
    // by construction the fields the office was told would be sent.
    if (!intake.ok || blockedReason) {
      setErr(blockedReason);
      return;
    }
    setBusy(true);
    setErr(null);
    const body: LolerReportCreateInput = {
      ...intake.fields,
      inspectorName: form.inspectorName.trim() || undefined,
      examinationDate: form.examinationDate || undefined,
      nextExaminationDue: form.nextExaminationDue || undefined,
      reportRef: form.reportRef.trim() || undefined,
      completeness: form.completeness,
      defects: defects.filter((d) => d.description.trim()),
      ...buildLiftSpecFields(liftSpec),
      // Provenance (Session AVR, wired here by Session BAD): this report is recorded from THIS email and attachment, once.
      sourceMessageId: candidate.gmailMessageId,
      sourceFilename: candidate.filename,
      // The relationship contact (Session ALO): the email's OWN sender, forwarded only on the
      // pre-contract branch (a lift already has a known customer). The server decides whether it is
      // worth keeping as a second contact (never a known examining body's own mailbox, never the
      // same person as the duty holder) -- since Session ALP a sweep candidate is usually the duty
      // holder or an RTM director's own address rather than an inspection body's, so this is what
      // captures the genuine relationship contact case the LOLER spec calls out, not a routine no-op.
      ...(mode === "prospect" ? { senderName: candidate.emailFromName || undefined, senderEmail: candidate.emailFrom || undefined } : {}),
    };
    try {
      const report = await api.loler.createReport(body);
      setCreatedReportId(report.id);
      // File the PDF from Gmail. Non-fatal: the report exists either way. A failure is retryable
      // in place (the office never held the file, so "upload it from the report page" was not a
      // usable recovery and re-sweeping would only create a duplicate record).
      if (!(await fileAttachment(report.id))) setFileWarn(true);
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not create the report.");
      setBusy(false);
    }
  }

  async function retryFiling() {
    if (!createdReportId) return;
    setRefiling(true);
    const ok = await fileAttachment(createdReportId);
    setFileWarn(!ok);
    setRefiling(false);
  }

  const skipped = candidate.skipReason != null;
  const cardTint = candidate.alreadyImported
    ? "border-ele-purple-accent bg-ele-surface/60 opacity-70"
    : skipped
      ? "border-amber-500/40 bg-amber-500/5"
      : "border-ele-purple-accent bg-ele-surface";

  return (
    <div className={`rounded-ele border p-3 ${cardTint}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-heading text-ele-strong">{candidate.emailSubject || "(no subject)"}</p>
          <p className="text-xs text-ele-body">
            {candidate.emailFrom || "unknown sender"} · {formatDate(candidate.emailDate)}
          </p>
          <p className="mt-0.5 truncate text-xs text-ele-body">{candidate.filename}</p>
          {/* Read the source document before attesting a statutory record from it. The extraction
              carries no lift identifier, and one visit can examine several lifts at a site, so the
              PDF is the only way to be sure which lift this is. */}
          {!skipped ? (
            <a
              href={api.loler.gmailAttachmentHref(candidate.gmailMessageId, candidate.gmailAttachmentId, candidate.filename)}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block font-menu text-xs tracking-wide text-ele-blue hover:underline"
            >
              View PDF
            </a>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {candidate.alreadyImported ? (
            <span className="rounded-full bg-ele-inset px-2 py-0.5 font-menu text-[10px] uppercase tracking-wide text-ele-body">Possible duplicate</span>
          ) : null}
          {created ? <span className="rounded-full bg-ele-inset px-2 py-0.5 font-menu text-[10px] uppercase tracking-wide text-ele-green">Report created</span> : null}
        </div>
      </div>

      {skipped ? (
        <p className="mt-2 rounded-ele border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-400">
          Could not read. {SKIP_MESSAGE[candidate.skipReason!] ?? "This attachment could not be read automatically."}
        </p>
      ) : (
        <>
          {candidate.duplicateReason ? (
            <p className="mt-2 rounded-ele border border-ele-purple-accent bg-ele-inset px-3 py-2 text-xs text-ele-body">
              {DUPLICATE_MESSAGE[candidate.duplicateReason] ?? "A similar report is already on record."} You can still create it if this is a different lift.
            </p>
          ) : null}
          {/* The site name is a HINT for finding the lift (the server reads the site from the chosen
              lift, never from this text), so it stays display-only. */}
          <p className="mt-3 text-xs text-ele-body">
            Read from the PDF{h.siteName ? <> for <span className="text-ele-strong">{h.siteName}</span></> : null}. Check it against the report and correct anything the reader got wrong.
          </p>

          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div>
              <label className={labelCls} htmlFor={`exam-${keyOf(candidate)}`}>Examination date</label>
              <input id={`exam-${keyOf(candidate)}`} type="date" className={inputCls} value={form.examinationDate} disabled={busy} onChange={(e) => setField("examinationDate", e.target.value)} />
            </div>
            <div>
              <label className={labelCls} htmlFor={`due-${keyOf(candidate)}`}>Next exam due</label>
              <input id={`due-${keyOf(candidate)}`} type="date" className={inputCls} value={form.nextExaminationDue} disabled={busy} onChange={(e) => setField("nextExaminationDue", e.target.value)} />
            </div>
            <div>
              <label className={labelCls} htmlFor={`insp-${keyOf(candidate)}`}>Inspector</label>
              <input id={`insp-${keyOf(candidate)}`} className={inputCls} value={form.inspectorName} disabled={busy} onChange={(e) => setField("inspectorName", e.target.value)} placeholder="e.g. ELTI" />
            </div>
            <div>
              <label className={labelCls} htmlFor={`ref-${keyOf(candidate)}`}>Report reference</label>
              <input id={`ref-${keyOf(candidate)}`} className={inputCls} value={form.reportRef} disabled={busy} onChange={(e) => setField("reportRef", e.target.value)} />
            </div>
            <div>
              <label className={labelCls} htmlFor={`comp-${keyOf(candidate)}`}>Completeness</label>
              <select id={`comp-${keyOf(candidate)}`} className={inputCls} value={form.completeness} disabled={busy} onChange={(e) => setField("completeness", e.target.value as ReportCompleteness)}>
                <option value="full">Full report</option>
                <option value="partial">Partial extract</option>
              </select>
            </div>
          </div>

          <div className="mt-3">
            <p className="mb-1 font-menu text-[10px] uppercase tracking-wide text-ele-body">Lift specification (optional)</p>
            <LiftSpecFields
              className="grid grid-cols-1 gap-2 sm:grid-cols-3"
              idPrefix={`spec-${keyOf(candidate)}`}
              labelCls={labelCls}
              inputCls={inputCls}
              disabled={busy}
              value={liftSpec}
              onChange={(patch) => setLiftSpec((f) => ({ ...f, ...patch }))}
            />
          </div>

          <div className="mt-3">
            <div className="mb-1 flex items-center justify-between">
              <p className="font-menu text-[10px] uppercase tracking-wide text-ele-body">Defects ({defects.length})</p>
              <button
                type="button"
                disabled={busy}
                onClick={() => setDefects((d) => [...d, { description: "", category: "advisory", deadline: null }])}
                className="font-menu text-xs tracking-wide text-ele-green hover:underline disabled:opacity-50"
              >
                + Add defect
              </button>
            </div>
            {defects.length === 0 ? <p className="text-xs text-ele-body">No defects were read from this report. Add any the report lists.</p> : null}
            <div className="space-y-2">
              {defects.map((d, i) => (
                <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_130px_130px_auto]">
                  <input
                    className={inputCls}
                    aria-label={`Defect ${i + 1} description`}
                    placeholder="Defect description"
                    value={d.description}
                    disabled={busy}
                    onChange={(e) => setDefects((arr) => arr.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
                  />
                  <select
                    className={inputCls}
                    aria-label={`Defect ${i + 1} category`}
                    value={d.category}
                    disabled={busy}
                    onChange={(e) => setDefects((arr) => arr.map((x, j) => (j === i ? { ...x, category: e.target.value as LolerDefectCategory } : x)))}
                  >
                    {(Object.keys(CATEGORY_LABEL) as LolerDefectCategory[]).map((c) => (
                      <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
                    ))}
                  </select>
                  {/* A "Remedy by date" item is only ever chased when it carries a deadline. The
                      extractor pre-fills one where the report states it; otherwise it is entered here. */}
                  <input
                    type="date"
                    className={inputCls}
                    aria-label={`Defect ${i + 1} remedy by`}
                    value={d.deadline ?? ""}
                    disabled={busy}
                    onChange={(e) => setDefects((arr) => arr.map((x, j) => (j === i ? { ...x, deadline: e.target.value || null } : x)))}
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setDefects((arr) => arr.filter((_, j) => j !== i))}
                    className="rounded-ele border border-ele-purple-accent px-3 text-xs text-ele-body hover:text-ele-danger disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {created ? (
        <div className="mt-3">
          <p className={`text-xs ${fileWarn ? "text-amber-400" : "text-ele-green"}`}>
            {fileWarn
              ? "Report created. The PDF could not be filed to Drive."
              : "Report created and the PDF filed. Open the LOLER reports list to view it."}
          </p>
          {/* The office never held this file, so re-uploading was never a usable recovery and
              re-sweeping would only create a second record. Retry the filing in place instead. */}
          {fileWarn ? (
            <button
              type="button"
              onClick={retryFiling}
              disabled={refiling}
              className="mt-2 rounded-ele border border-ele-blue px-4 py-1.5 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-blue/10 disabled:opacity-50"
            >
              {refiling ? "Filing..." : "Try filing again"}
            </button>
          ) : null}
        </div>
      ) : (
        <>
          {err ? <p role="alert" className="mt-2 text-xs text-ele-danger">{err}</p> : null}
          {/* WHY Create is not live, on screen rather than in a tooltip. Create is deliberately
              disabled until the record is fileable (an examination date is what makes it statutory,
              Session AGF), and a disabled control's title attribute never paints in Chromium or
              WebKit (Session AIE), so without this the office is left with a dead button and no
              reason. It doubles as the prompt that there IS a second path, on a card that has just
              opened with nothing chosen. */}
          {!busy && blockedReason ? <p className="mt-2 text-xs text-ele-body">{blockedReason}</p> : null}
          {suggestPreContract ? (
            <div className="mt-3 rounded-ele border border-amber-500/40 bg-amber-500/10 p-2">
              <p className="text-xs text-amber-400">
                No lift on file matches this search, and the report names a customer or a site. If this examination is for
                somebody not on the platform yet, record it as a pre-contract enquiry so those details are saved with the
                report and the lead is tracked. Choosing a lift keeps this as an ordinary report and hides this note.
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() => setMode("prospect")}
                className="mt-1 font-menu text-xs tracking-wide text-amber-400 underline disabled:opacity-50"
              >
                Switch to pre-contract enquiry
              </button>
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap items-end gap-2">
            {!skipped ? (
              <div className="min-w-0 grow">
                {/* The same two choices, in the same words, as the manual New report modal. */}
                <div className="mb-2 flex flex-wrap gap-2">
                  {(["lift", "prospect"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      disabled={busy}
                      aria-pressed={mode === m}
                      onClick={() => setMode(m)}
                      className={`rounded-ele border px-3 py-1.5 font-menu text-[11px] tracking-wide max-sm:min-h-11 disabled:opacity-50 ${mode === m ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body"}`}
                    >
                      {m === "lift" ? "Existing lift" : "Pre-contract enquiry"}
                    </button>
                  ))}
                </div>
                {mode === "lift" ? (
                  <>
                    <label className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-body" htmlFor={`sweep-lift-${keyOf(candidate)}`}>
                      Lift for this report
                    </label>
                    <RecordPicker
                      kind="lift"
                      id={`sweep-lift-${keyOf(candidate)}`}
                      value={assetId}
                      onChange={(id) => setAssetId(id)}
                      filters={{ includeRetired: false }}
                      initialQuery={siteTerm}
                      ariaLabel="Choose lift"
                      placeholder="Search by ELE number, site or customer"
                      disabled={busy}
                    />
                  </>
                ) : (
                  <PreContractFields
                    className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                    idPrefix={`pre-${keyOf(candidate)}`}
                    labelCls={labelCls}
                    inputCls={inputCls}
                    disabled={busy}
                    value={pre}
                    onChange={(patch) => setPre((p) => ({ ...p, ...patch }))}
                  />
                )}
              </div>
            ) : null}
            <div className="flex gap-2">
              {!skipped ? (
                <Button
                  size="sm"
                  onClick={create}
                  disabled={busy || !canCreate}
                  // The same sentence rendered visibly beside the button, not a second copy of the
                  // rule. A disabled control's title never paints in Chromium or WebKit (Session
                  // AIE), so this is a courtesy for a hovering mouse, never the explanation.
                  title={blockedReason ?? undefined}
                >
                  {busy ? "Creating..." : "Create report"}
                </Button>
              ) : null}
              <button
                type="button"
                onClick={onDismiss}
                disabled={busy}
                className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-50"
              >
                Dismiss
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

